import { useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useGT } from 'gt-react';
import { toast } from 'src/components/ui/toast';
import { Action, Subject } from '@erp/authz';
import { useCan } from 'src/hooks/useCan';
import { useUnsavedChangesGuard } from 'src/hooks/useUnsavedChangesGuard';
import {
  useCreateTaskType,
  useTaskTypes,
} from 'src/api/rpc/hooks/tasks/useTaskTypes';
import {
  buildTaskTypeBehavior,
  emptyEditorState,
  isValidTaskTypeKey,
  taskTypeKeyFromName,
  type TaskTypeEditorState,
} from '../components/cards/task-types/taskTypeAdmin';
import { stageOptionsFromTaskTypes } from '../components/cards/task-types/taskTypeStageTree';
import {
  canSaveTaskTypeEditor,
  isTaskTypeEditorDirty,
  mutationErrorMessage,
} from './taskTypeDetailEditor';
import { useMetricFieldOptions } from '../components/cards/task-types/useMetricFieldOptions';

/**
 * Full-form "new task type" editor: the same TaskTypeEditorState the detail
 * page edits, held locally until a single create call persists everything
 * (identity, behavior, stages, actions). On success we navigate back to the
 * task types list.
 */
export function useTaskTypeCreateEditor() {
  const gt = useGT();
  const navigate = useNavigate();
  const [canManage] = useCan(Action.Create, Subject.TaskType);

  const { data: taskTypes } = useTaskTypes();
  const createTaskType = useCreateTaskType();

  const [editor, setEditor] = useState<TaskTypeEditorState>(emptyEditorState);
  // Once the user edits the key directly, stop suggesting it from the name.
  const keyTouched = useRef(false);

  const dirty = isTaskTypeEditorDirty(editor);
  const unsavedGuard = useUnsavedChangesGuard(dirty);
  const metricFields = useMetricFieldOptions(editor);
  const canSave =
    canSaveTaskTypeEditor({ canManage, editor, metricFields }) &&
    isValidTaskTypeKey(editor.key);

  function setDraft(next: TaskTypeEditorState) {
    if (next.key !== editor.key) {
      keyTouched.current = true;
    }
    setEditor(next);
  }

  function handleNameChange(name: string) {
    setEditor((current) => ({
      ...current,
      name,
      key: keyTouched.current ? current.key : taskTypeKeyFromName(name),
    }));
  }

  function returnToList() {
    void navigate({
      to: '/settings/organization/$section',
      params: { section: 'task-types' },
    });
  }

  async function handleCreate() {
    if (!canSave) return;
    try {
      const created = await createTaskType.mutateAsync({
        key: editor.key.trim(),
        name: editor.name.trim(),
        description: editor.description.trim() || null,
        ...buildTaskTypeBehavior(editor),
      });
      toast.success(
        gt('Task type created', {
          context: 'toast after creating a task type',
        }),
      );
      // Continue on the new type's page so the task page and actions can be
      // set up without going back through the list. The blocker still sees
      // the pre-create dirty state in this tick.
      void navigate({
        to: '/settings/organization/task-types/$taskTypeId',
        params: { taskTypeId: created.id },
        ignoreBlocker: true,
      });
    } catch (error) {
      toast.error(
        mutationErrorMessage(
          error,
          gt('Failed to create the task type', {
            context: 'toast after a task type create failed',
          }),
        ),
      );
    }
  }

  const stageOptions = stageOptionsFromTaskTypes(taskTypes ?? []);

  return {
    canManage,
    editor,
    canSave,
    isDirty: dirty,
    setDraft,
    handleNameChange,
    stageOptions,
    unsavedGuard,
    handleBack: () => unsavedGuard.guard(() => returnToList()),
    handleCreate,
    isCreating: createTaskType.isPending,
  };
}
